import axios from 'axios';

const rawBaseUrl = process.env.REACT_APP_API_BASE_URL;
if (!rawBaseUrl) {
  throw new Error('Missing required env var: REACT_APP_API_BASE_URL');
}
const baseURL = rawBaseUrl.endsWith('/') ? rawBaseUrl.slice(0, -1) : rawBaseUrl;

const api = axios.create({
  baseURL,
  withCredentials: true,  // ✅ Permitir envío de cookies y credenciales
  timeout: 60000,         // ✅ 60 segundos para requests largos (subidas a Dropbox con archivado de versión previa)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Interceptor para agregar el token de autorización y logging CORS
api.interceptors.request.use((config) => {
  // Solo usar sessionStorage para permitir múltiples usuarios en diferentes pestañas
  const token = sessionStorage.getItem('authToken');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log(`✅ Token agregado: ${config.method?.toUpperCase()} ${config.url}`);
  } else {
    console.log(`⚠️ Sin token: ${config.method?.toUpperCase()} ${config.url}`);
  }

  // Logging para debugging CORS
  console.log(`🌐 Request CORS: ${config.baseURL}${config.url}`, {
    withCredentials: config.withCredentials,
    headers: config.headers
  });

  return config;
}, (error) => {
  console.error('❌ Request interceptor error:', error);
  return Promise.reject(error);
});

// Archivos que iban en la petición (FormData), para el mensaje y el log del 413
const uploadedFiles = (data) => {
  if (typeof FormData === 'undefined' || !(data instanceof FormData)) return [];
  return [...data.values()].filter((v) => typeof File !== 'undefined' && v instanceof File);
};
const formatMb = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

// 413 del proxy de entrada (nginx): responde HTML y la petición nunca llega al backend, así que
// el backend no lo registra. Se pone un mensaje claro en error.response.data (lo que ya muestran
// las pantallas) y se avisa al backend para que quede en su log. Un 413 del propio backend
// (límite de Spring) ya trae su mensaje en JSON y se respeta.
const handlePayloadTooLarge = (error) => {
  const response = error.response;
  if (response?.data && typeof response.data === 'object' && response.data.error) return;
  const files = error.config?.uploadFiles || [];
  const sizeBytes = files.reduce((sum, f) => sum + f.size, 0);
  const fileName = files.map((f) => f.name).join(', ');
  const message = files.length
    ? `El archivo "${fileName}" (${formatMb(sizeBytes)}) excede el tamaño que permite el servidor. Intenta con un archivo más ligero o avisa al administrador del sistema.`
    : 'La información enviada excede el tamaño que permite el servidor. Avisa al administrador del sistema.';
  response.data = { error: message, message };
  console.error('❌ 413 del servidor (proxy) -- archivo rechazado antes de llegar al backend:', {
    url: error.config?.url, method: error.config?.method, fileName, sizeBytes,
  });
  api.post('/client-events/upload-too-large', {
    url: error.config?.url, method: (error.config?.method || '').toUpperCase(), fileName, sizeBytes,
  }).catch(() => {});
};

// Se toman antes de que axios serialice el cuerpo (transformRequest)
api.interceptors.request.use((config) => {
  const files = uploadedFiles(config.data);
  if (files.length) config.uploadFiles = files;
  return config;
});

// Interceptor para manejar respuestas y errores CORS
api.interceptors.response.use(
  (response) => {
    console.log(`✅ Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`);
    return response;
  },
  (error) => {
    if (error.response?.status === 413) handlePayloadTooLarge(error);

    // Logging detallado de errores
    if (error.code === 'ERR_NETWORK' || error.message.includes('CORS')) {
      console.error('🚨 Error CORS detectado:', {
        message: error.message,
        code: error.code,
        url: error.config?.url,
        method: error.config?.method
      });
    }
    
    const isAuthEndpoint = error.config?.url?.includes('/users/login');
    // El motor de sincronización offline (offlineSyncEngine.js) manda skipAuthRedirect: true --
    // si la sesión venció mientras el inspector estaba sin señal, NO queremos redirigir a mitad
    // de una sincronización en segundo plano (perdería el contexto de la pantalla sin avisar
    // por qué). Ese motor maneja su propio 401 y conserva el borrador local en IndexedDB.
    if (error.response?.status === 401 && !isAuthEndpoint && !error.config?.skipAuthRedirect) {
      console.log('🔐 Token expirado, redirigiendo al login...');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userInfo');
      // Sin el query param, esta redirección es silenciosa: el usuario pierde lo que
      // estaba haciendo (ej. subir un archivo) sin ninguna explicación de por qué.
      window.location.href = '/login?error=sesion_expirada';
    } else {
      console.error(`❌ Response error: ${error.response?.status || 'Network'} ${error.config?.method?.toUpperCase()} ${error.config?.url}`);
    }
    
    return Promise.reject(error);
  }
);

// "¿Quién soy?" (GET /users/me) lo piden por separado muchos componentes al cargar una misma
// pantalla (Navbar, la página, diálogos). Se comparte la misma respuesta durante 30 s por sesión
// (llave = token), así varias peticiones simultáneas se vuelven una sola. Cualquier cambio a
// usuarios o empresas invalida la copia para no mostrar datos viejos.
const ME_TTL_MS = 30000;
let meCache = null; // { token, at, promise }
const originalGet = api.get.bind(api);
api.get = (url, config) => {
  if (url !== '/users/me' || config?.params) return originalGet(url, config);
  const token = sessionStorage.getItem('authToken');
  const now = Date.now();
  if (meCache && meCache.token === token && now - meCache.at < ME_TTL_MS) return meCache.promise;
  const promise = originalGet(url, config).catch((error) => {
    meCache = null;
    throw error;
  });
  meCache = { token, at: now, promise };
  return promise;
};
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get' && /^\/(users|companies)/.test(config.url || '')) meCache = null;
  return config;
});

export default api;
